import React, { useMemo } from 'react';
import { Card, Descriptions, Tag, Space, Alert, Statistic, Row, Col, Typography } from 'antd';
import { ArrowUpOutlined, BankOutlined, WarningOutlined } from '@ant-design/icons';
import { formatAmount, getActualLoanData, resolveEssPayload, toNumber } from './essPayload';

const { Text } = Typography;

interface ESSTopUpInfoViewProps {
    data: any;
    formikProps?: any;
}

const ESSTopUpInfoView: React.FC<ESSTopUpInfoViewProps> = ({ data, formikProps }) => {
    const actualData = getActualLoanData(data, formikProps);

    const isTopUpLoan = actualData?.loanType === 'TOPUP';

    const essPayload = useMemo(() => {
        return resolveEssPayload(actualData);
    }, [actualData?.essRequestPayload, actualData?.formattedEssRequestPayload]);

    if (!isTopUpLoan || !essPayload) {
        return null;
    }

    const previousOutstandingBalance = toNumber(
        actualData?.previousOutstandingBalance || actualData?.payoffAmountToFsp1 || 0
    );
    const accruedInterest = toNumber(actualData?.unpaidAccruedInterest || 0);
    const additionalAmount = toNumber(
        actualData?.topUpAmount || essPayload.requestedAmount || essPayload.additionalAmount || 0
    );
    const newLoanAmount = toNumber(actualData?.loanAmt || actualData?.recommendedLoanAmt || 0);
    const newTotalAmountToPay = toNumber(actualData?.totalAmountToPay || 0);
    const otherRepaymentCharges = toNumber(actualData?.otherCharges || 0);
    const topUpFee = toNumber(actualData?.topUpFee || 0);
    const processingFee = toNumber(actualData?.processingFee || 0);
    const newMonthlyPayment = toNumber(
        actualData?.newInstallment || actualData?.monthlyReturnAmount || 0
    );
    const newTenure = actualData?.lnPeriod || actualData?.recommendedLnPeriod || essPayload.tenure || 0;
    const newInterestRate = actualData?.intRate || essPayload.interestRate || 0;

    return (
        <Card
            title={
                <Space>
                    <ArrowUpOutlined />
                    <span>Loan Top-Up Information</span>
                </Space>
            }
            className="mb-4"
        >
            <Alert
                message="Loan Top-Up Transaction"
                description="This application settles the existing loan and creates a new loan. The new total amount to pay includes the new principal, interest, and any repayment charges."
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                className="mb-4"
            />

            <Card type="inner" title="Top-Up Financial Breakdown" className="mb-4">
                <Row gutter={16}>
                    <Col xs={24} sm={8}>
                        <Statistic
                            title="Previous Outstanding Balance"
                            value={previousOutstandingBalance}
                            precision={2}
                            prefix="TZS"
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic
                            title="Additional Amount Requested"
                            value={additionalAmount}
                            precision={2}
                            prefix="TZS"
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic
                            title="New Total Amount to Pay"
                            value={newTotalAmountToPay}
                            precision={2}
                            prefix="TZS"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Col>
                </Row>

                <div className="mt-4">
                    <Text type="secondary">
                        New Loan Amount: <strong>TZS {formatAmount(newLoanAmount)}</strong> = Previous Outstanding (TZS {formatAmount(previousOutstandingBalance)}) + Additional (TZS {formatAmount(additionalAmount)})
                    </Text>
                </div>
            </Card>

            <Card
                type="inner"
                title={
                    <Space>
                        <BankOutlined />
                        <span>Existing Loan Details</span>
                    </Space>
                }
                className="mb-4"
            >
                <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                    <Descriptions.Item label="Previous Loan Number" span={2}>
                        <strong>{actualData?.previousLoanNumber || essPayload.existingLoanNumber || 'N/A'}</strong>
                    </Descriptions.Item>

                    <Descriptions.Item label="Previous Outstanding Balance">
                        <Tag color="red">TZS {formatAmount(previousOutstandingBalance)}</Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Unpaid Accrued Interest">
                        TZS {formatAmount(accruedInterest)}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card type="inner" title="New Loan Terms">
                <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                    <Descriptions.Item label="New Loan Amount">
                        <strong>TZS {formatAmount(newLoanAmount)}</strong>
                    </Descriptions.Item>

                    <Descriptions.Item label="New Total Amount to Pay">
                        <Tag color="green">TZS {formatAmount(newTotalAmountToPay)}</Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="New Monthly Payment">
                        TZS {formatAmount(newMonthlyPayment)}
                    </Descriptions.Item>

                    <Descriptions.Item label="New Tenure (Months)">
                        {newTenure}
                    </Descriptions.Item>

                    <Descriptions.Item label="New Interest Rate (%)">
                        {newInterestRate}
                    </Descriptions.Item>

                    <Descriptions.Item label="Top-Up Fee (Insurance & Others)">
                        TZS {formatAmount(topUpFee)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Processing Fee">
                        TZS {formatAmount(processingFee)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Other Repayment Charges">
                        TZS {formatAmount(otherRepaymentCharges)}
                    </Descriptions.Item>
                </Descriptions>

                <Alert
                    message="New Total Amount to Pay"
                    description={
                        <div>
                            <p>
                                <strong>TZS {formatAmount(newTotalAmountToPay)}</strong> includes the new loan amount (TZS {formatAmount(newLoanAmount)}), total interest, top-up fees (TZS {formatAmount(topUpFee)}), processing fees (TZS {formatAmount(processingFee)}), and any other repayment charges (TZS {formatAmount(otherRepaymentCharges)}).
                            </p>
                        </div>
                    }
                    type="info"
                    showIcon
                    className="mt-4"
                />
            </Card>
        </Card>
    );
};

export default ESSTopUpInfoView;
